import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import Navbar from "@/components/Navbar";
import AnnouncementBar from "@/components/AnnouncementBar";
import Footer from "@/components/Footer";
import PetCareAI from "@/components/PetCareAI";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import WelcomeGiftModal from "@/components/WelcomeGiftModal";
import StorefrontPWA from "@/components/StorefrontPWA";
import "@/i18n";
import { initGoogleTranslate } from "@/i18n/googleTranslate";

// If the user previously picked a non-English language, the googtrans cookie
// is set and Google Translate starts auto-translating on page load. We init
// its script early so we can suppress the banner before it flashes.
if (typeof document !== "undefined" && /googtrans=/.test(document.cookie || "") && !/googtrans=\/auto\/en/.test(document.cookie || "")) {
  initGoogleTranslate();
}

// HomePage is the most-visited landing surface — keep it eager so the first
// paint isn't gated on a chunk fetch. Every other route is code-split: the
// browser only downloads the JS for the page the user actually navigates to.
import HomePage from "@/pages/HomePage";

const ShopPage             = lazy(() => import("@/pages/ShopPage"));
const ProductDetail        = lazy(() => import("@/pages/ProductDetail"));
const AboutPage            = lazy(() => import("@/pages/AboutPage"));
const BlogPage             = lazy(() => import("@/pages/BlogPage"));
const BlogDetail           = lazy(() => import("@/pages/BlogDetail"));
const ContactPage          = lazy(() => import("@/pages/ContactPage"));
const CartPage             = lazy(() => import("@/pages/CartPage"));
const CheckoutPage         = lazy(() => import("@/pages/CheckoutPage"));
const CheckoutSuccess      = lazy(() => import("@/pages/CheckoutSuccess"));
const AdminLogin           = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard       = lazy(() => import("@/pages/AdminDashboard"));

const LoginPage            = lazy(() => import("@/pages/LoginPage"));
const SignupPage           = lazy(() => import("@/pages/SignupPage"));
const ForgotPasswordPage   = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage    = lazy(() => import("@/pages/ResetPasswordPage"));
const VerifyEmailPage      = lazy(() => import("@/pages/VerifyEmailPage"));

const PrivacyPolicyPage    = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsPage            = lazy(() => import("@/pages/TermsPage"));

const AccountLayout        = lazy(() => import("@/pages/account/AccountLayout"));
const ProfileSection       = lazy(() => import("@/pages/account/ProfileSection"));
const PetsSection          = lazy(() => import("@/pages/account/PetsSection"));
const AddressesSection     = lazy(() => import("@/pages/account/AddressesSection"));
const WishlistSection      = lazy(() => import("@/pages/account/WishlistSection"));
const OrdersSection        = lazy(() => import("@/pages/account/OrdersSection"));
const OrderDetailPage      = lazy(() => import("@/pages/account/OrderDetailPage"));
const SubscriptionsSection = lazy(() => import("@/pages/account/SubscriptionsSection"));

import { CartProvider } from "@/lib/cart";
import { AuthProvider } from "@/lib/auth";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Tiny fallback shown for the brief moment a route chunk is fetching.
// Matches the cream background and existing minimal "Loading…" pattern
// already used by AccountLayout, so it never feels like a different app.
function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-cream">
      <div className="animate-pulse text-ink-muted text-sm">Loading…</div>
    </div>
  );
}

function Shell({ children }) {
  const { pathname } = useLocation();
  const hideChrome = pathname.startsWith("/admin");
  const showWelcomeGift = pathname === "/";
  return (
    <div className="min-h-screen flex flex-col bg-cream">
      {!hideChrome && <AnnouncementBar />}
      {!hideChrome && <Navbar />}
      <main className="flex-1">{children}</main>
      {!hideChrome && <Footer />}
      {!hideChrome && <PetCareAI />}
      {!hideChrome && <WhatsAppFAB />}
      {!hideChrome && <StorefrontPWA />}
      {showWelcomeGift && <WelcomeGiftModal />}
    </div>
  );
}

// All OAuth callbacks now happen on the backend at /api/auth/google/callback
// — the backend issues the JWT cookie and 302-redirects to /account.
function AppRouter() {
  return (
    <Shell>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/product/:slug" element={<ProductDetail />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />

          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          <Route path="/account" element={<AccountLayout />}>
            <Route index element={<ProfileSection />} />
            <Route path="pets" element={<PetsSection />} />
            <Route path="addresses" element={<AddressesSection />} />
            <Route path="wishlist" element={<WishlistSection />} />
            <Route path="orders" element={<OrdersSection />} />
            <Route path="orders/:session_id" element={<OrderDetailPage />} />
            <Route path="subscriptions" element={<SubscriptionsSection />} />
          </Route>

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </Suspense>
    </Shell>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Toaster position="top-center" richColors />
            <AppRouter />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
